import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { collection, addDoc, getDocs, GeoPoint } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { CURRENCY_OPTIONS, getCurrencySymbol } from '../currencyOptions';
import { validateLotForm } from '../lotValidation';

interface AddParkingLotDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendorId?: string; // When set, auto-assigns to this vendor and hides the vendor selector
}

interface CurrencyPricing {
  rate: string;  // Rate per period (hour/day/week/month)
}

export default function AddParkingLotDialog({ open, onClose, onSuccess, vendorId: fixedVendorId }: AddParkingLotDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    lotName: '',
    address: '',
    latitude: '',
    longitude: '',
    timezone: 'America/New_York',
    vendorId: '',
    ratePeriod: 'hour',  // hour, day, week, or month
    salesTaxRate: '',
    maxOvertimeHours: '',
    // Grace window in minutes between session expiry and full session end.
    // Snapshotted onto each session at creation; mid-session lot edits do
    // not affect already-active sessions. Default 10 if left blank.
    gracePeriodMinutes: '10',
    hasAssignedSpaces: true,
    totalSpots: ''
  });

  // Multi-currency pricing state
  const [currencies, setCurrencies] = useState<Record<string, CurrencyPricing>>({
    USD: { rate: '' }
  });

  const [selectedCurrencyToAdd, setSelectedCurrencyToAdd] = useState('');

  // Admin-only. Demo lots are hidden from the mobile app's location list.
  const [isDemo, setIsDemo] = useState(false);

  // Get all IANA timezones supported by the browser
  const ALL_TIMEZONES: { value: string; label: string }[] = (Intl as any).supportedValuesOf('timeZone').map((tz: string) => ({
    value: tz,
    label: tz.replace(/_/g, ' ')
  }));

  const [vendors, setVendors] = useState<any[]>([]);
  const [generatedCode, setGeneratedCode] = useState('');

  React.useEffect(() => {
    const fetchVendors = async () => {
      try {
        const vendorsSnapshot = await getDocs(collection(db, 'vendors'));
        const vendorsList = vendorsSnapshot.docs.map(doc => ({
          vendorId: doc.data().vendorId,
          companyName: doc.data().companyName
        }));
        setVendors(vendorsList);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    // Only the admin view renders the partner selector. Partners can't read
    // the vendors collection at all, so fetching it from their dialog just
    // produced a permission error on every open.
    if (open && !fixedVendorId) {
      fetchVendors();
    }
  }, [open, fixedVendorId]);

  const generateLotCode = async (): Promise<string> => {
    const safeAlphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ';

    const lotsSnapshot = await getDocs(collection(db, 'parkinglots'));
    const existingCodes = lotsSnapshot.docs
      .map(doc => doc.data().lotCode)
      .filter(code => code);

    for (let i = 0; i < 100; i++) {
      const code =
        safeAlphabet[Math.floor(Math.random() * 23)] +
        safeAlphabet[Math.floor(Math.random() * 23)];

      if (!existingCodes.includes(code)) {
        return code;
      }
    }

    throw new Error('Could not generate unique lot code after 100 attempts');
  };

  const handleAddCurrency = () => {
    if (selectedCurrencyToAdd && !currencies[selectedCurrencyToAdd]) {
      setCurrencies({
        ...currencies,
        [selectedCurrencyToAdd]: { rate: '' }
      });
      setSelectedCurrencyToAdd('');
    }
  };

  const handleRemoveCurrency = (code: string) => {
    const { [code]: removed, ...rest } = currencies;
    setCurrencies(rest);
  };

  const handleCurrencyPricingChange = (code: string, field: keyof CurrencyPricing, value: string) => {
    setCurrencies({
      ...currencies,
      [code]: { ...currencies[code], [field]: value }
    });
  };

  const handleSubmit = async () => {
    // fixedVendorId is set when this dialog is mounted from the partner
    // dashboard. Partner-created lots start in pending_review and must be
    // approved by an admin before they go live. Admin-created lots are
    // approved immediately.
    const isPartnerContext = !!fixedVendorId;

    // The submit button is a plain button, not a form submit, so the
    // `required` props never fire. Validate here or Firestore ends up with
    // NaN rates and empty lot names.
    const problems = validateLotForm(formData, currencies);
    if (problems.length > 0) {
      alert('Please fix the following before saving:\n\n• ' + problems.join('\n• '));
      return;
    }

    try {
      const lotCode = await generateLotCode();
      setGeneratedCode(lotCode);

      // Create GeoPoint if coordinates provided
      let location = null;
      if (formData.latitude && formData.longitude) {
        location = new GeoPoint(
          parseFloat(formData.latitude),
          parseFloat(formData.longitude)
        );
      }

      // Calculate initial availability = total spots
      const totalSpots = parseInt(formData.totalSpots);

      // Build currencies object for Firestore
      const currenciesData: Record<string, any> = {};
      Object.entries(currencies).forEach(([code, pricing]) => {
        currenciesData[code] = {
          rate: pricing.rate ? parseFloat(pricing.rate) : null,
        };
      });

      await addDoc(collection(db, 'parkinglots'), {
        lotCode: lotCode,
        lotName: formData.lotName,
        address: formData.address,
        latlon: location,
        timezone: formData.timezone,
        vendorId: fixedVendorId || formData.vendorId || null,

        // Approval state. Mobile app should treat anything other than
        // 'approved' as not bookable. Legacy lots (no status field) are
        // implicitly approved.
        status: isPartnerContext ? 'pending_review' : 'approved',

        // Demo lots are hidden from the mobile app's location list.
        isDemo: isPartnerContext ? false : isDemo,

        // Pricing configuration
        ratePeriod: formData.ratePeriod,  // hour, day, week, or month
        currencies: currenciesData,
        salesTaxRate: parseFloat(formData.salesTaxRate),

        // Overtime configuration
        maxOvertimeHours: formData.maxOvertimeHours ? parseFloat(formData.maxOvertimeHours) : null,

        // Grace window (minutes). Each session snapshots this at creation.
        gracePeriodMinutes: formData.gracePeriodMinutes ? parseInt(formData.gracePeriodMinutes, 10) : 10,

        // Space configuration
        hasAssignedSpaces: formData.hasAssignedSpaces,
        totalSpots: totalSpots,
        availability: totalSpots,

        createdAt: new Date()
      });

      if (isPartnerContext) {
        alert(
          'Your parking lot has been submitted for review.\n\n' +
          "We'll verify the details and finish setting up your payment " +
          'gateway, then notify you when the lot is approved and ready ' +
          'to accept parkers.'
        );
      } else {
        alert(`Parking lot created successfully! Lot Code: ${lotCode}`);
      }

      // Reset form
      setFormData({
        lotName: '',
        address: '',
        latitude: '',
        longitude: '',
        timezone: 'America/New_York',
        vendorId: '',
        ratePeriod: 'hour',
        salesTaxRate: '',
        maxOvertimeHours: '',
        gracePeriodMinutes: '10',
        hasAssignedSpaces: true,
        totalSpots: ''
      });
      setCurrencies({
        USD: { rate: '' }
      });
      setGeneratedCode('');
      setIsDemo(false);

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding parking lot:', error);
      alert('Failed to add parking lot: ' + (error as Error).message);
    }
  };

  const availableCurrencies = CURRENCY_OPTIONS.filter(c => !currencies[c.code]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Add New Parking Lot</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {generatedCode && !fixedVendorId && (
            <Alert severity="success">
              Generated Lot Code: <strong>{generatedCode}</strong>
            </Alert>
          )}

          {/* Basic Information */}
          <Typography variant="h6">Basic Information</Typography>
          <Divider />

          <TextField
            fullWidth
            label="Lot Name"
            value={formData.lotName}
            onChange={(e) => setFormData({ ...formData, lotName: e.target.value })}
            required
          />

          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            required
          />

          <FormControl fullWidth required>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              label="Timezone"
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {ALL_TIMEZONES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
              size={isMobile ? 'small' : 'medium'}
            />

            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>

          {!fixedVendorId && (
            <FormControl fullWidth>
              <InputLabel>Partner</InputLabel>
              <Select
                value={formData.vendorId}
                onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                label="Partner"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.vendorId} value={vendor.vendorId}>
                    {vendor.companyName} (ID: {vendor.vendorId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Pricing Configuration */}
          <Typography variant="h6" sx={{ mt: 2 }}>Pricing Configuration</Typography>
          <Divider />

          <FormControl fullWidth required>
            <InputLabel>Rate Period</InputLabel>
            <Select
              value={formData.ratePeriod}
              onChange={(e) => setFormData({ ...formData, ratePeriod: e.target.value })}
              label="Rate Period"
            >
              <MenuItem value="hour">Per Hour</MenuItem>
              <MenuItem value="day">Per Day</MenuItem>
              <MenuItem value="week">Per Week</MenuItem>
              <MenuItem value="month">Per Month</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Sales Tax Rate"
            type="number"
            value={formData.salesTaxRate}
            onChange={(e) => setFormData({ ...formData, salesTaxRate: e.target.value })}
            required
            helperText="Decimal format (e.g., 0.08 for 8%)"
            inputProps={{ step: "0.01", min: "0", max: "1" }}
          />

          <TextField
            fullWidth
            label="Max Overtime Hours"
            type="number"
            value={formData.maxOvertimeHours}
            onChange={(e) => setFormData({ ...formData, maxOvertimeHours: e.target.value })}
            helperText="Max overtime hours to bill after session expires (leave blank for no limit)"
            inputProps={{ step: "0.5", min: "0" }}
          />

          <TextField
            fullWidth
            label="Grace Period (minutes)"
            type="number"
            value={formData.gracePeriodMinutes}
            onChange={(e) => setFormData({ ...formData, gracePeriodMinutes: e.target.value })}
            helperText="Minutes after expiry the user can still Add Time without losing the spot. Snapshotted per session at creation — changing this only affects new sessions."
            inputProps={{ step: "1", min: "0" }}
          />

          {/* Currency Pricing */}
          <Typography variant="h6" sx={{ mt: 2 }}>Currency Pricing</Typography>
          <Divider />

          <Alert severity="info">
            Add one or more currencies with their respective pricing. Users will choose their preferred currency at checkout.
          </Alert>

          {/* Add Currency */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Add Currency</InputLabel>
              <Select
                value={selectedCurrencyToAdd}
                onChange={(e) => setSelectedCurrencyToAdd(e.target.value)}
                label="Add Currency"
                size="small"
              >
                {availableCurrencies.map((currency) => (
                  <MenuItem key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCurrency}
              disabled={!selectedCurrencyToAdd}
            >
              Add
            </Button>
          </Box>

          {/* Currency Pricing Cards */}
          {Object.entries(currencies).map(([code, pricing]) => (
            <Paper key={code} sx={{ p: 2, mt: 1 }} variant="outlined">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Chip
                  label={`${code} (${getCurrencySymbol(code)})`}
                  color="primary"
                  variant="outlined"
                />
                {Object.keys(currencies).length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveCurrency(code)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <TextField
                fullWidth
                label={`Rate per ${formData.ratePeriod} (${getCurrencySymbol(code)})`}
                type="number"
                value={pricing.rate}
                onChange={(e) => handleCurrencyPricingChange(code, 'rate', e.target.value)}
                required
                size="small"
              />
            </Paper>
          ))}

          {/* Space Configuration */}
          <Typography variant="h6" sx={{ mt: 2 }}>Space Configuration</Typography>
          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={formData.hasAssignedSpaces}
                onChange={(e) => setFormData({ ...formData, hasAssignedSpaces: e.target.checked })}
              />
            }
            label="Has Assigned Parking Spaces"
          />

          <Alert severity="info">
            {formData.hasAssignedSpaces
              ? "Users will enter specific space codes (e.g., PQ045)"
              : "General admission - users enter lot code only (e.g., PQAAA)"}
          </Alert>

          <TextField
            fullWidth
            label="Total Parking Spots"
            type="number"
            value={formData.totalSpots}
            onChange={(e) => setFormData({ ...formData, totalSpots: e.target.value })}
            required
            helperText="Total capacity of the parking lot"
          />

          {!fixedVendorId && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDemo}
                    onChange={(e) => setIsDemo(e.target.checked)}
                  />
                }
                label="Demo Lot"
              />
              {isDemo && (
                <Alert severity="info">
                  Demo lots are hidden from the Locations list in the mobile app.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Add Parking Lot
        </Button>
      </DialogActions>
    </Dialog>
  );
}
